import { RefObject, useCallback, useEffect, useRef } from 'react';

import { ScrollController } from './scroll-controller';
import { ScrollControllerPayload } from './types';

export interface UseScrollControllerParams {
    /**
     * Экземпляр ScrollController
     */
    controller: ScrollController;
    /**
     * Ref на скроллируемый контейнер
     */
    scrollContainerRef: RefObject<HTMLDivElement>;
    /**
     * Флаг активации отслеживания состояния скролла
     * @default false
     */
    trackScrollState?: boolean;
}

/**
 * Хук для подключения скролл-контроллера к React компоненту
 *
 * @param params - Объект с параметрами:
 *   - controller: экземпляр ScrollController
 *   - scrollContainerRef: ref на скроллируемый контейнер
 *   - trackScrollState: флаг активации отслеживания состояния скролла (по умолчанию false)
 *
 */
export function useScrollController(params: UseScrollControllerParams) {
    const { controller, scrollContainerRef, trackScrollState = false } = params;
    useEffect(() => {
        controller.setScrollHandler((payload: ScrollControllerPayload) => {
            const container = scrollContainerRef.current;
            if (!container) return;

            const containerHeight = container.clientHeight;
            const scrollHeight = container.scrollHeight;
            const currentScrollTop = container.scrollTop;
            const maxScrollTop = scrollHeight - containerHeight;

            const children = container.children;
            const firstChild = children.length > 0 ? (children[0] as HTMLElement) : null;
            const elementHeight = firstChild?.getBoundingClientRect().height || 0;

            let newScrollTop: number;

            switch (payload.type) {
                case 'screen': {
                    if (!firstChild) return;

                    if (payload.direction === 'down') {
                        // Вычисляем индекс последнего видимого элемента
                        const lastVisibleIndex = Math.min(
                            Math.floor((currentScrollTop + containerHeight - 1) / elementHeight),
                            children.length - 1
                        );

                        // Устанавливаем этот элемент первым сверху
                        newScrollTop = Math.min(maxScrollTop, lastVisibleIndex * elementHeight);
                    } else {
                        // Вычисляем индекс первого видимого элемента
                        const firstVisibleIndex = Math.max(0, Math.floor(currentScrollTop / elementHeight));

                        // Устанавливаем этот элемент последним снизу
                        newScrollTop = Math.max(0, (firstVisibleIndex + 1) * elementHeight - containerHeight);
                    }
                    break;
                }
                case 'elements': {
                    if (!firstChild) return;

                    const scrollDistance = payload.count * elementHeight;

                    if (payload.direction === 'up') {
                        newScrollTop = Math.max(0, currentScrollTop - scrollDistance);
                    } else {
                        newScrollTop = Math.min(maxScrollTop, currentScrollTop + scrollDistance);
                    }

                    // Выравнивание по границам элементов
                    const targetIndex = Math.floor(newScrollTop / elementHeight);
                    const clampedIndex = Math.max(0, Math.min(targetIndex, children.length - 1));

                    const targetElement = children[clampedIndex] as HTMLElement;
                    const elementRect = targetElement.getBoundingClientRect();
                    const containerRect = container.getBoundingClientRect();

                    newScrollTop = elementRect.top - containerRect.top + container.scrollTop;
                    break;
                }
                case 'start': {
                    newScrollTop = 0;
                    break;
                }
                case 'end': {
                    newScrollTop = maxScrollTop;
                    break;
                }
                default:
                    return;
            }

            container.scrollTo({
                top: newScrollTop,
                behavior: 'smooth',
            });
        });

        return () => {
            controller.removeScrollHandler();
        };
    }, [controller, scrollContainerRef]);

    const lastScrollStateRef = useRef({ canScrollUp: false, canScrollDown: false });

    const checkScrollState = useCallback(() => {
        const container = scrollContainerRef.current;
        if (!container) {
            return;
        }

        const scrollTop = container.scrollTop;
        const scrollHeight = container.scrollHeight;
        const clientHeight = container.clientHeight;

        const canScrollUp = scrollTop > 0;
        const canScrollDown = scrollTop < scrollHeight - clientHeight - 1;

        const lastState = lastScrollStateRef.current;
        if (lastState.canScrollUp !== canScrollUp || lastState.canScrollDown !== canScrollDown) {
            lastScrollStateRef.current = { canScrollUp, canScrollDown };
            controller.onScrollStateChanged.dispatch({ canScrollUp, canScrollDown });
        }
    }, [controller, scrollContainerRef]);

    useEffect(() => {
        if (!trackScrollState) {
            return;
        }

        const container = scrollContainerRef.current;
        if (!container) {
            return;
        }

        checkScrollState();

        container.addEventListener('scroll', checkScrollState);

        // ResizeObserver для отслеживания изменения размера контейнера
        const resizeObserver = new ResizeObserver(() => {
            requestAnimationFrame(checkScrollState);
        });
        resizeObserver.observe(container);

        // MutationObserver для отслеживания добавления/удаления дочерних элементов
        const mutationObserver = new MutationObserver(() => {
            requestAnimationFrame(checkScrollState);
        });
        mutationObserver.observe(container, { childList: true });

        return () => {
            container.removeEventListener('scroll', checkScrollState);
            resizeObserver.disconnect();
            mutationObserver.disconnect();
        };
    }, [checkScrollState, trackScrollState, scrollContainerRef]);
}
